import { Box, IconButton } from '@material-ui/core';
import { CloseIcon } from '@room-match/shared-ui-components';
import clsx from 'clsx';
import React, { useImperativeHandle } from 'react';
import ReactPlayer from 'react-player';
import { VideoControl } from '../../components/VideoControls';
import { useCustomVideoPlayer } from '../../shared/hooks/useCustomVideoPlayer';
import { VideoPlayerRef } from '../../shared/interfaces/VideoPlayerRef';
import { useStyles } from './CustomVideoPlayer.styles';
import Loader from './Loader';

type Props = {
  url: string;
  startTime?: number;
  endTime?: number;
  thumbnail?: string | boolean;
  playIcon?: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  classes?: {
    root: string;
    videoPlayer: string;
    icon: string;
  };
  key?: string;
  onEnded?: () => void;
  autoPlay?: boolean;
  isSmall?: boolean;
};

const CustomVideoPlayer = React.forwardRef<VideoPlayerRef, Props>(
  ({ url, thumbnail, playIcon, classes: customClass, key, autoPlay, onEnded, isSmall }, ref) => {
    const classes = useStyles();
    const {
      playerRef,
      isFullScreen,
      isVideoPlaying,
      isVideoReady,
      isBuffering,
      currentVideoTime,
      videoProgress,
      volume,
      videoDuration,
      showControls,
      bufferedProgress,
      handleReady,
      handlePlay,
      handlePause,
      handleEnded,
      handleProgress,
      handleSliderChange,
      handleVolumeChange,
      handleOpenFullScreen,
      handleCloseFullScreen,
      handleBufferStart,
      handleBufferEnd,
      handleStartScrub,
      handleEndScrub,
      handleCaptureImage,
    } = useCustomVideoPlayer({
      autoPlay: autoPlay ? autoPlay : false,
    });

    useImperativeHandle(ref, () => ({
      captureImage: () => {
        return handleCaptureImage();
      },
    }));

    return (
      <Box
        style={{ cursor: !showControls && isFullScreen ? 'none' : 'default' }}
        className={clsx(
          classes.videoWrapper,
          {
            [classes.videoWrapperFullScreen]: isFullScreen,
          },
          customClass?.root,
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (e.target === playerRef?.current?.getInternalPlayer()) {
            if (isVideoPlaying) {
              handlePause();
            } else {
              handlePlay();
            }
          }

          // toggleVideoPlaying();
        }}
      >
        <ReactPlayer
          volume={volume}
          key={key}
          ref={playerRef}
          className={clsx('react-player', customClass?.videoPlayer)}
          url={url}
          width="100%"
          height={isFullScreen ? '100%' : '88%'}
          playing={isVideoPlaying}
          onReady={handleReady}
          onPause={handlePause}
          onEnded={() => {
            handleEnded();
            if (onEnded) {
              onEnded();
            }
          }}
          onProgress={handleProgress}
          playIcon={playIcon}
          progressInterval={10}
          light={thumbnail ? thumbnail : undefined}
          onBuffer={handleBufferStart}
          onBufferEnd={handleBufferEnd}
          config={{
            file: {
              attributes: {
                crossorigin: 'anonymous',
              },
            },
          }}
        />

        {autoPlay && <Loader isLoading={!isVideoReady || isBuffering} />}

        {/* {isShowVideoAction && (
      <Box className={classes.playAction}>
        <IconButton onClick={() => toggleVideoPlaying()} style={{ backgroundColor: '#fff' }}>
          {isVideoPlaying ? <StopIcon htmlColor="#fff" /> : <PlayArrowOutlinedIcon htmlColor="#fff" />}
        </IconButton>
      </Box>
    )} */}
        {isVideoReady && (
          <React.Fragment>
            {!isFullScreen ? (
              <div className={classes.controlsContainer}>
                <VideoControl
                  videoProgress={videoProgress}
                  onSliderChange={handleSliderChange}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  volume={volume}
                  onChangeVolume={handleVolumeChange}
                  isVideoPlaying={isVideoPlaying}
                  currentVideoTime={currentVideoTime}
                  videoDuration={videoDuration}
                  isFullScreen={isFullScreen}
                  onOpenFullScreen={handleOpenFullScreen}
                  onCloseFullScreen={handleCloseFullScreen}
                  isBuffering={isBuffering}
                  isSmall={isSmall}
                  onEndedScrub={handleEndScrub}
                  onStartScrub={handleStartScrub}
                  bufferedProgress={bufferedProgress}
                />
              </div>
            ) : (
              <React.Fragment>
                {showControls && (
                  <VideoControl
                    videoProgress={videoProgress}
                    onSliderChange={handleSliderChange}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    volume={volume}
                    onChangeVolume={handleVolumeChange}
                    isVideoPlaying={isVideoPlaying}
                    currentVideoTime={currentVideoTime}
                    videoDuration={videoDuration}
                    isFullScreen={isFullScreen}
                    onOpenFullScreen={handleOpenFullScreen}
                    onCloseFullScreen={handleCloseFullScreen}
                    isBuffering={isBuffering}
                    onEndedScrub={handleEndScrub}
                    onStartScrub={handleStartScrub}
                    bufferedProgress={bufferedProgress}
                  />
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}

        {isVideoReady && isFullScreen && showControls && (
          <div className={classes.closeButtonWrapper}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleCloseFullScreen();
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        )}
      </Box>
    );
  },
);

export default CustomVideoPlayer;
